"use client";
import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CourseRun } from "@/common/courseRun/types";
import ExecEdEnrollButton from "@/components/CourseContent/ExecEdEnrollButton";
import CTALink from "@/components/CTA/ctaLink";
import { MonarchDataConsumer } from "@/monarch/MonarchDataConsumer";
import { ExecEdStickyCTATestResult } from "@/monarch/rules/execEdStickyCTA/types";

type Props = {
  links: { label: string; path: string }[];
  courseRun: CourseRun | null;
  requestMoreInfoButton?: { label: string; path: string } | null;
  marketingType?: string;
  enrollButtonLink: URL | string;
  subject?: string;
};
export default function ExecEdStickyNav({
  links,
  courseRun,
  requestMoreInfoButton,
  marketingType = undefined,
  enrollButtonLink,
  subject,
}: Props) {
  const router = useRouter();
  const ref = useRef<HTMLDivElement>(null);
  const [isNavOnTop, setIsNavOnTop] = useState(false);

  useEffect(() => {
    if (window.scrollY === ref.current?.offsetTop) {
      setIsNavOnTop(true);
    }
    const onScroll = () => {
      if (ref.current?.offsetTop) {
        const isTop = Math.abs(window.scrollY - ref.current?.offsetTop) < 36;
        setIsNavOnTop(isTop);
      }
    };
    // clean up code
    window.removeEventListener("scroll", onScroll);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <MonarchDataConsumer<ExecEdStickyCTATestResult>
      experimentKey="useExecEdStickyCTA"
      render={(testResult) => {
        const { data } = testResult;
        return (
          <div
            className={`${data?.showMobile ? "block" : "hidden"} ${data?.showDesktop ? "md:block" : "md:hidden"} sticky top-0 w-screen ml-[calc(-50vw+50%)] z-50 shadow-xl`}
            ref={ref}
          >
            {isNavOnTop && courseRun?.title && (
              <div className={"bg-primary px-5"}>
                <h2 className="text-primary-foreground mx-auto py-2 text-sm font-bold leading-5 m-0">
                  {courseRun.title}
                </h2>
              </div>
            )}
            <div
              className={`${isNavOnTop ? "block" : "hidden"} bg-primary-500 px-5 flex`}
            >
              <div
                className={`px-4 mx-auto flex items-center gap-6 max-w-7xl w-full justify-center md:justify-between`}
              >
                <nav className="hidden md:flex py-4 mx-1 flex-wrap gap-x-10 text-white underline underline-offset-4">
                  {links.map((link) => (
                    <a href={link.path} key={`lg-${link.path}`}>
                      {link.label}
                    </a>
                  ))}
                </nav>
                <div
                  className={`hidden grow py-4 flex items-center justify-center`}
                >
                  <Select onValueChange={(e) => router.push(e)}>
                    <SelectTrigger className="bg-white text-base">
                      <SelectValue placeholder="Brochure" />
                    </SelectTrigger>
                    <SelectContent>
                      {links.map((link) => (
                        <SelectItem
                          key={`md-${link.path}`}
                          value={link.path}
                          className="text-base"
                        >
                          {link.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-row gap-7 my-2">
                  {isNavOnTop && marketingType === "sprint" && courseRun && (
                    <ExecEdEnrollButton
                      courseRun={courseRun}
                      enrollButtonLink={enrollButtonLink}
                      onStickyNav={true}
                    />
                  )}
                  {isNavOnTop && requestMoreInfoButton && (
                    <CTALink
                      label={data?.ctaText || requestMoreInfoButton.label}
                      url={requestMoreInfoButton.path}
                      type="brand"
                      enable_tracking={true}
                      className="inline-block text-base min-w-full md:min-w-32 py-2 px-4 min-h-11 whitespace-nowrap"
                      location="sticky-banner"
                      html_id="request-more-info-cta"
                      name="sticky-cta"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      }}
    />
  );
}
